import { useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Carte, MessageErreur } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import {
  urlJustificatifAbsence,
  useDeciderJustificatif,
  type JustificatifAExaminer,
} from '../../lib/useAbsences';
import { useToast } from '../ui/useToast';

function dateLisible(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * Justificatifs déposés par les apprenants depuis leur espace, en attente de
 * l'examen du pôle. Accepté, les demi-journées deviennent justifiées ; refusé,
 * l'apprenant reçoit le motif. Le fichier s'ouvre par une route gardée, chaque
 * lecture est tracée.
 */
export function CarteJustificatifs({
  justificatifs,
}: {
  justificatifs: JustificatifAExaminer[];
}): JSX.Element | null {
  const toast = useToast();
  const decider = useDeciderJustificatif();
  const [aRefuser, setARefuser] = useState<JustificatifAExaminer | null>(null);
  const [motifRefus, setMotifRefus] = useState('');

  if (justificatifs.length === 0) return null;

  return (
    <Carte titre={`Justificatifs à examiner (${justificatifs.length})`} sansBordure>
      {decider.error instanceof ErreurApi && !aRefuser && (
        <div className="p-4 sm:px-6">
          <MessageErreur message={decider.error.message} />
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {justificatifs.map((justificatif) => (
          <li key={justificatif.id} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            <div className="min-w-[12rem] flex-1">
              <div className="font-semibold text-codialis-navy">
                {justificatif.apprenant}
                <span className="ml-2 text-[11px] font-normal text-codialis-gray">{justificatif.siteCode}</span>
              </div>
              <div className="text-xs text-codialis-gray">
                {justificatif.motifLibelle} · déposé le {dateLisible(justificatif.deposeLe)}
              </div>
              <div className="text-[11px] text-codialis-gray">{justificatif.demiJournees.join(' · ')}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={urlJustificatifAbsence(justificatif.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-codialis-sky hover:underline"
              >
                <Paperclip className="h-4 w-4" aria-hidden="true" />
                Lire
              </a>
              <BoutonSecondaire
                disabled={decider.isPending}
                onClick={() => {
                  setMotifRefus('');
                  setARefuser(justificatif);
                }}
              >
                Refuser
              </BoutonSecondaire>
              <BoutonPrincipal
                disabled={decider.isPending}
                onClick={() =>
                  decider.mutate(
                    { id: justificatif.id, decision: 'ACCEPTER' },
                    { onSuccess: (reponse) => toast(reponse.message) },
                  )
                }
              >
                Accepter
              </BoutonPrincipal>
            </div>
          </li>
        ))}
      </ul>

      <Modale
        titre="Refuser le justificatif"
        sousTitre={aRefuser ? `${aRefuser.apprenant} · ${aRefuser.nomFichier}` : undefined}
        ouverte={aRefuser !== null}
        onFermer={() => setARefuser(null)}
        largeur="max-w-lg"
      >
        <div className="space-y-3 p-4 sm:p-6">
          <label htmlFor="motif-refus" className="block text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Motif du refus, transmis à l’apprenant
          </label>
          <textarea
            id="motif-refus"
            value={motifRefus}
            onChange={(evenement) => setMotifRefus(evenement.target.value)}
            rows={3}
            maxLength={500}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none"
            placeholder="Ex. document illisible, dates ne couvrant pas l’absence"
          />
          {decider.error instanceof ErreurApi && <MessageErreur message={decider.error.message} />}
          <div className="flex flex-wrap justify-end gap-2">
            <BoutonSecondaire onClick={() => setARefuser(null)}>Annuler</BoutonSecondaire>
            <BoutonPrincipal
              disabled={!motifRefus.trim() || decider.isPending || !aRefuser}
              onClick={() =>
                aRefuser &&
                decider.mutate(
                  { id: aRefuser.id, decision: 'REFUSER', motifRefus: motifRefus.trim() },
                  {
                    onSuccess: (reponse) => {
                      toast(reponse.message);
                      setARefuser(null);
                    },
                  },
                )
              }
            >
              Refuser et prévenir l’apprenant
            </BoutonPrincipal>
          </div>
        </div>
      </Modale>
    </Carte>
  );
}
